// "Place the number" questions (22 Oct 2026, Year 2): the question names a
// number and the child shows where it goes on a number line. This is the
// "tap a point on the number line" answer (numberLineAnswer.ts) with two
// things added for younger children: big "Move left" / "Move right" buttons
// under the line (a tick is a narrow thing to tap on a phone), and a rule
// that the tick to find carries no number and no letter, so the line never
// shows the answer. Sent, stored and marked exactly as a tapped point: the
// one entry "p=<value>" (for example "p=7"), by the existing set_match rule.

import { numberLineKey, numberLineTick } from './numberLineAnswer';
import { readPracticeDiagram } from './practice-diagram-data';
import type { NumberLineSpec } from './practice-number-line-data';

// The answer key text for a place question as the question bank writes it
// (the number, and the question's number line), for whatever loads the
// questions. The same text as a tapped point's (numberLineKey). Null when
// the answer is not a tick of the line, or its tick is numbered or lettered
// on the line, which would give the answer away.
export function placeKey(answer: unknown, picture: unknown): string | null {
  const key = numberLineKey(answer, picture);
  if (key === null) return null;
  const line = readPracticeDiagram({ diagram: picture });
  if (line?.type !== 'number_line') return null;
  const at = numberLineTick(line, answer);
  if (at === null) return null;
  const index = (at - line.min) / line.step;
  if (index % line.labelEvery === 0) return null;
  if (line.marks.some((mark) => mark.at === at)) return null;
  return key;
}

// The dot moved one tick along the line by "Move left" (-1) or "Move right"
// (1), stopping at the ends. With no dot yet, "Move right" puts it at the
// start of the line and "Move left" at the end, as if it came in from that
// side.
export function movePlace(
  line: Pick<NumberLineSpec, 'min' | 'max' | 'step'>,
  chosen: number | null,
  direction: -1 | 1,
): number {
  if (chosen === null) return direction === 1 ? line.min : line.max;
  return Math.min(line.max, Math.max(line.min, chosen + direction * line.step));
}
